import React from 'react';
import {
  ErrorBox,
  ErrorIcon,
  GenericMessageContent,
  GenericMessageHeading,
  GenericMessageSubText,
  GenericMessageAdvice,
} from './styled';
import { colours } from 'utils/theme';
import { useNlpOrgContext } from './NlpOrgContext';

// ai-search prefixes guardrails explanations; the heading already says the search failed.
const REJECTION_PREFIX = /^query rejected:\s*/i;

// Other reasons (dangerous, offensive, vague) can echo the user's input, so they stay hidden.
const SHOWN_REJECTION_REASON = 'irrelevant';

// Shared generic copy for the "we can't act on this query" cases — guardrails rejection
// (QueryRejected) and unsupported dimension (UnsupportedFeature, which also covers
// unresolvable non-tradeshow/organisation lookups). A non-committal message — bold heading
// then sub-text + advice as plain lines — so we never name a capability we might
// actually hold. EntityNotFound keeps its own data-gap copy.
const GenericUnsupportedMessage = ({ testId, explanation }) => (
  <GenericMessageContent data-testid={testId}>
    <GenericMessageHeading>
      We couldn't complete this search.
    </GenericMessageHeading>
    {explanation ? (
      <GenericMessageSubText data-testid='nlp-rejection-explanation'>
        {explanation}
      </GenericMessageSubText>
    ) : (
      <GenericMessageSubText>
        SciLeads might not hold data for what you're looking for, or it may fall
        outside the filters we support.
      </GenericMessageSubText>
    )}
    <GenericMessageAdvice>
      Try rephrasing your query, or search by Contacts, Organisations or
      Activities.
    </GenericMessageAdvice>
    <GenericMessageAdvice>
      Including key scientific terms will improve your results.
    </GenericMessageAdvice>
  </GenericMessageContent>
);

export const NLPAPIErrorBox = ({ apiError }) => {
  // Null on main search, which never shows guardrails explanations.
  const isOrgContext = useNlpOrgContext() !== null;

  // Compute the explanation based upon the error code we received
  const explanation = React.useMemo(() => {
    if (apiError.errorCode === 'AISearch.QueryRejected') {
      const showsExplanation =
        isOrgContext && apiError.rejectionReason === SHOWN_REJECTION_REASON;
      const serverExplanation = showsExplanation
        ? (apiError.explanation ?? '').replace(REJECTION_PREFIX, '').trim()
        : '';
      return <GenericUnsupportedMessage explanation={serverExplanation} />;
    }

    // ai-search emits EntityNotFound only for lookups it treats as a genuine data gap,
    // so here we name the unmatched entities; everything else arrives as
    // UnsupportedFeature and falls through to the shared generic message above.
    if (apiError.errorCode === 'AISearch.EntityNotFound') {
      if (apiError.notFoundEntities?.length > 0) {
        return (
          <span data-testid='nlp-not-found-entities'>
            We don't have data for {apiError.notFoundEntities.join(', ')} yet.
          </span>
        );
      } else {
        return (
          <span data-testid='nlp-not-found-entities'>
            We don't yet have data for some of the search terms you requested.
          </span>
        );
      }
    }

    if (apiError.errorCode === 'AISearch.UnsupportedFeature') {
      return <GenericUnsupportedMessage testId='nlp-unsupported-features' />;
    }

    if (apiError.errorCode === 'AISearch.UnsupportedNegation') {
      if (apiError.unsupportedNegations?.length > 0) {
        // Not perfect — a workaround for the only checkbox-style filter that
        // behaves this way right now. `excludeRecentlyExportedEmails` is
        // exclusion-only: you can exclude recently-exported emails, but can't
        // filter to show only them. The generic "excluding ..." copy below
        // misreads for that direction, so special-case it here. If more
        // exclusion-only filters appear, replace this string match with a
        // structured signal from the API.
        //
        // Only when it's the sole negation — if other unsupported negations
        // came back too, the dedicated one-liner would hide them, so fall
        // through to the generic list instead.
        if (
          apiError.unsupportedNegations.length === 1 &&
          apiError.unsupportedNegations[0]
            .toLowerCase()
            .includes('recently-exported emails')
        ) {
          return (
            <span data-testid='nlp-unsupported-negations'>
              You can exclude recently exported emails, but can't filter to show
              only them.
            </span>
          );
        }
        return (
          <span data-testid='nlp-unsupported-negations'>
            We don't yet support excluding{' '}
            {apiError.unsupportedNegations.join(', ')}.
          </span>
        );
      }
      return (
        <span data-testid='nlp-unsupported-negations'>
          We don't yet support excluding the items you asked to exclude.
        </span>
      );
    }

    // SCIL-15937: the AI generated a filter the user's licence doesn't cover (an out-of-licence
    // organisation category, or a Venture Capital / Acquisitions / Organisation Metrics /
    // sponsored-trials filter). Shown on stage 1 before a credit is spent.
    if (apiError.errorCode === 'AISearch.LicenseRequired') {
      const upgradeLink = (
        <a
          href='https://scileads.chilipiper.com/shared/laura-haldane/sales-demo'
          target='_blank'
          rel='noopener noreferrer'
        >
          Upgrade your licence
        </a>
      );
      if (apiError.unsupportedFeatures?.length > 0) {
        return (
          <span data-testid='nlp-license-required'>
            Your licence doesn't include{' '}
            {apiError.unsupportedFeatures.join(', ')}. {upgradeLink}.
          </span>
        );
      }
      return (
        <span data-testid='nlp-license-required'>
          Your search uses filters your licence doesn't include. {upgradeLink}.
        </span>
      );
    }

    // The following error codes could be supported with a different message
    // in future, but for now will fall back to the generic error.
    // if (apiError.errorCode === "AISearch.FilterGenerationFailed")
    // if (apiError.errorCode === "AISearch.InvalidEntityType")
    // if (apiError.errorCode === "AISearch.ServiceUnavailable")
    // if (apiError.errorCode === "AISearch.InternalError")

    // Fall back to generic error message
    return (
      <span>
        Something went wrong while processing your request, please refresh the
        page and try again. The issue has been reported to our team, but if you
        continue to receive this error please reach out to us via live chat.
      </span>
    );
  }, [apiError, isOrgContext]);

  const isGenericMessage =
    apiError.errorCode === 'AISearch.QueryRejected' ||
    apiError.errorCode === 'AISearch.UnsupportedFeature';

  return (
    <ErrorBox $alignTop={isGenericMessage}>
      <ErrorIcon
        icon='error'
        size='sm'
        color={colours.character.warning}
        $alignTop={isGenericMessage}
      />
      {explanation}
    </ErrorBox>
  );
};
